import { ChevronRight } from "@mui/icons-material";
import IconEdit from "@mui/icons-material/Edit";
import { Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Radio } from "@mui/material";
import { useTranslation } from "react-i18next";

import { MapBoundsKind, MapCenterAddress, MapCenterMode } from "./types";

type MapCenterOptionsMenuProps = {
    mode: MapCenterMode;
    boundsKind: MapBoundsKind;
    address: MapCenterAddress | null;
    onSelectBounds: () => void;
    onSelectAddress: (address: MapCenterAddress) => void;
    onEditAddress: () => void;
};

export function MapCenterOptionsMenu({
    mode,
    boundsKind,
    address,
    onSelectBounds,
    onSelectAddress,
    onEditAddress,
}: MapCenterOptionsMenuProps) {
    const { t } = useTranslation("map");

    // without a saved address there is nothing to select, so go straight to the address panel
    const handleSelectAddress = () => {
        if (address) {
            onSelectAddress(address);
        } else {
            onEditAddress();
        }
    };

    return (
        <List role="radiogroup" aria-label={t("mapButtonFitBounds.openOptions")} disablePadding>
            <ListItem role="none" disablePadding>
                <ListItemButton
                    role="radio"
                    aria-checked={mode === "bounds"}
                    autoFocus={mode === "bounds"}
                    onClick={onSelectBounds}
                >
                    <OptionRadio checked={mode === "bounds"} />
                    <ListItemText primary={t(`mapButtonFitBounds.option.${boundsKind}`)} />
                </ListItemButton>
            </ListItem>

            <Divider component="li" role="none" />

            <ListItem
                role="none"
                disablePadding
                secondaryAction={
                    <IconButton edge="end" size="small" aria-label={t("mapButtonFitBounds.editAddress")} onClick={onEditAddress}>
                        {address ? <IconEdit fontSize="small" /> : <ChevronRight />}
                    </IconButton>
                }
            >
                <ListItemButton
                    role="radio"
                    aria-checked={mode === "address"}
                    autoFocus={mode === "address"}
                    onClick={handleSelectAddress}
                >
                    <OptionRadio checked={mode === "address"} />
                    <ListItemText
                        primary={t("mapButtonFitBounds.option.address")}
                        secondary={address?.shortAddress}
                        secondaryTypographyProps={{ noWrap: true }}
                    />
                </ListItemButton>
            </ListItem>
        </List>
    );
}

function OptionRadio({ checked }: { checked: boolean }) {
    return (
        <ListItemIcon sx={{ minWidth: 0, marginRight: 1.5 }}>
            <Radio
                checked={checked}
                size="small"
                tabIndex={-1}
                disableRipple
                sx={{ padding: 0 }}
                inputProps={{ "aria-hidden": true }}
            />
        </ListItemIcon>
    );
}
